import React, { useState } from 'react';
import { XIcon, PlusIcon, TrashIcon } from 'lucide-react';
interface AddInventoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (inventoryData: any) => void;
}
export function AddInventoryModal({
  isOpen,
  onClose,
  onSave
}: AddInventoryModalProps) {
  const [roomType, setRoomType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [availableRooms, setAvailableRooms] = useState('');
  const [closedToArrivalDates, setClosedToArrivalDates] = useState<string[]>([]);
  const [closedToDepartureDates, setClosedToDepartureDates] = useState<string[]>([]);
  const [blackoutDates, setBlackoutDates] = useState<string[]>([]);
  const [newClosedToArrivalDate, setNewClosedToArrivalDate] = useState('');
  const [newClosedToDepartureDate, setNewClosedToDepartureDate] = useState('');
  const [newBlackoutDate, setNewBlackoutDate] = useState('');
  if (!isOpen) return null;
  const handleAddClosedToArrivalDate = () => {
    if (newClosedToArrivalDate && !closedToArrivalDates.includes(newClosedToArrivalDate)) {
      setClosedToArrivalDates([...closedToArrivalDates, newClosedToArrivalDate]);
      setNewClosedToArrivalDate('');
    }
  };
  const handleAddClosedToDepartureDate = () => {
    if (newClosedToDepartureDate && !closedToDepartureDates.includes(newClosedToDepartureDate)) {
      setClosedToDepartureDates([...closedToDepartureDates, newClosedToDepartureDate]);
      setNewClosedToDepartureDate('');
    }
  };
  const handleAddBlackoutDate = () => {
    if (newBlackoutDate && !blackoutDates.includes(newBlackoutDate)) {
      setBlackoutDates([...blackoutDates, newBlackoutDate]);
      setNewBlackoutDate('');
    }
  };
  const handleRemoveClosedToArrivalDate = (date: string) => {
    setClosedToArrivalDates(closedToArrivalDates.filter(d => d !== date));
  };
  const handleRemoveClosedToDepartureDate = (date: string) => {
    setClosedToDepartureDates(closedToDepartureDates.filter(d => d !== date));
  };
  const handleRemoveBlackoutDate = (date: string) => {
    setBlackoutDates(blackoutDates.filter(d => d !== date));
  };
  const handleSubmit = () => {
    const inventoryData = {
      roomType,
      fromDate,
      toDate,
      availableRooms: parseInt(availableRooms, 10) || 0,
      closedToArrivalDates,
      closedToDepartureDates,
      blackoutDates
    };
    onSave(inventoryData);
    onClose();
  };
  return <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-2282">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-2283">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-2284">
          <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-2285"></div>
        </div>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-2286">
          <div className="flex justify-between items-center border-b border-gray-200 px-6 py-4" data-id="element-2287">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-2288">Add Inventory</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500" data-id="element-2289">
              <XIcon size={20} data-id="element-2290" />
            </button>
          </div>
          <div className="p-6" data-id="element-2291">
            <p className="text-sm text-gray-500 mb-4" data-id="element-2292">
              Select the room type and applicable inventory rules
            </p>
            <div className="space-y-4" data-id="element-2293">
              {/* Room Type */}
              <div data-id="element-2294">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2295">
                  Room Type*
                </label>
                <div className="relative" data-id="element-2296">
                  <select className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 appearance-none focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={roomType} onChange={e => setRoomType(e.target.value)} data-id="element-2297">
                    <option value="" data-id="element-2298">Select room type...</option>
                    <option value="premium-overwater" data-id="element-2299">
                      Premium Overwater Villa
                    </option>
                    <option value="water-villa" data-id="element-2300">Water Villa</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-2301">
                    <ChevronDownIcon size={16} className="text-gray-400" data-id="element-2302" />
                  </div>
                </div>
              </div>
              {/* From Date */}
              <div data-id="element-2303">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2304">
                  From*
                </label>
                <input type="text" placeholder="MM/DD/YYYY" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={fromDate} onChange={e => setFromDate(e.target.value)} data-id="element-2305" />
              </div>
              {/* To Date */}
              <div data-id="element-2306">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2307">
                  To*
                </label>
                <input type="text" placeholder="MM/DD/YYYY" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={toDate} onChange={e => setToDate(e.target.value)} data-id="element-2308" />
              </div>
              {/* Available Rooms */}
              <div data-id="element-2309">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2310">
                  Daily Available Rooms*
                </label>
                <input type="number" placeholder="Enter daily available rooms" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={availableRooms} onChange={e => setAvailableRooms(e.target.value)} data-id="element-2311" />
              </div>
              {/* Closed to Arrival dates */}
              <div data-id="element-2312">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2313">
                  Closed to Arrival dates in this period
                </label>
                <div className="space-y-2" data-id="element-2314">
                  {closedToArrivalDates.map((date, index) => <div key={index} className="flex items-center" data-id="element-2315">
                      <input type="text" value={date} readOnly className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50" data-id="element-2316" />
                      <button onClick={() => handleRemoveClosedToArrivalDate(date)} className="ml-2 text-red-500" data-id="element-2317">
                        <TrashIcon size={16} data-id="element-2318" />
                      </button>
                    </div>)}
                  <div className="flex items-center" data-id="element-2319">
                    <input type="text" placeholder="MM/DD/YYYY" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newClosedToArrivalDate} onChange={e => setNewClosedToArrivalDate(e.target.value)} data-id="element-2320" />
                    <button onClick={handleAddClosedToArrivalDate} className="ml-2 text-red-500" data-id="element-2321">
                      <TrashIcon size={16} data-id="element-2322" />
                    </button>
                  </div>
                  <button onClick={handleAddClosedToArrivalDate} className="flex items-center text-red-600 text-sm font-medium" data-id="element-2323">
                    <PlusIcon size={16} className="mr-1" data-id="element-2324" />
                    Add
                  </button>
                </div>
              </div>
              {/* Closed to Departure dates */}
              <div data-id="element-2325">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2326">
                  Closed to Departure dates in this period
                </label>
                <div className="space-y-2" data-id="element-2327">
                  {closedToDepartureDates.map((date, index) => <div key={index} className="flex items-center" data-id="element-2328">
                      <input type="text" value={date} readOnly className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50" data-id="element-2329" />
                      <button onClick={() => handleRemoveClosedToDepartureDate(date)} className="ml-2 text-red-500" data-id="element-2330">
                        <TrashIcon size={16} data-id="element-2331" />
                      </button>
                    </div>)}
                  <div className="flex items-center" data-id="element-2332">
                    <input type="text" placeholder="MM/DD/YYYY" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newClosedToDepartureDate} onChange={e => setNewClosedToDepartureDate(e.target.value)} data-id="element-2333" />
                    <button onClick={handleRemoveClosedToDepartureDate} className="ml-2 text-red-500" data-id="element-2334">
                      <TrashIcon size={16} data-id="element-2335" />
                    </button>
                  </div>
                  <button onClick={handleAddClosedToDepartureDate} className="flex items-center text-red-600 text-sm font-medium" data-id="element-2336">
                    <PlusIcon size={16} className="mr-1" data-id="element-2337" />
                    Add
                  </button>
                </div>
              </div>
              {/* Blackout dates */}
              <div data-id="element-2338">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2339">
                  Blackout dates in this period
                </label>
                <div className="space-y-2" data-id="element-2340">
                  {blackoutDates.map((date, index) => <div key={index} className="flex items-center" data-id="element-2341">
                      <input type="text" value={date} readOnly className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50" data-id="element-2342" />
                      <button onClick={() => handleRemoveBlackoutDate(date)} className="ml-2 text-red-500" data-id="element-2343">
                        <TrashIcon size={16} data-id="element-2344" />
                      </button>
                    </div>)}
                  <div className="flex items-center" data-id="element-2345">
                    <input type="text" placeholder="MM/DD/YYYY" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newBlackoutDate} onChange={e => setNewBlackoutDate(e.target.value)} data-id="element-2346" />
                    <button onClick={handleRemoveBlackoutDate} className="ml-2 text-red-500" data-id="element-2347">
                      <TrashIcon size={16} data-id="element-2348" />
                    </button>
                  </div>
                  <button onClick={handleAddBlackoutDate} className="flex items-center text-red-600 text-sm font-medium" data-id="element-2349">
                    <PlusIcon size={16} className="mr-1" data-id="element-2350" />
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-6 py-4 flex justify-end" data-id="element-2351">
            <button onClick={handleSubmit} className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-2352">
              Create
            </button>
          </div>
        </div>
      </div>
    </div>;
}
// Helper component for the ChevronDown icon
function ChevronDownIcon({
  size = 24,
  className = ''
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-2353">
      <polyline points="6 9 12 15 18 9" data-id="element-2354"></polyline>
    </svg>;
}